import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Button,
  Tooltip,
  VStack,
  HStack,
  Text,
  Progress,
} from '@chakra-ui/react'
import { QuestionOutlineIcon, ViewIcon, ViewOffIcon, CheckIcon } from '@chakra-ui/icons'
import { useState } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'

interface ControlPasswordWithValidationProps {
  register: UseFormRegisterReturn
  label: string
  placeholder?: string
  helpText?: string
  isInvalid?: boolean
  errorMessage?: string
  showStrength?: boolean
}

export default function ControlPasswordWithValidation({
  register,
  label,
  placeholder,
  helpText,
  isInvalid,
  errorMessage,
  showStrength = false,
}: ControlPasswordWithValidationProps) {
  const [show, setShow] = useState(false)
  const [password, setPassword] = useState('')
  const [isTouched, setIsTouched] = useState(false)

  const handleClick = () => setShow(!show)

  // Password strength checks
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  }

  const strengthScore = Object.values(checks).filter(Boolean).length
  const strengthPercent = (strengthScore / 4) * 100
  const strengthColor =
    strengthScore === 4 ? 'green' : strengthScore >= 2 ? 'orange' : 'red'
  const strengthLabel =
    strengthScore === 4
      ? 'Strong'
      : strengthScore >= 2
      ? 'Medium'
      : 'Weak'

  const showFeedback = showStrength && isTouched && password.length > 0

  return (
    <FormControl isInvalid={isInvalid}>
      <FormLabel display="flex" alignItems="center" gap={2} fontSize="sm" fontWeight="medium">
        {label}
        {helpText && (
          <Tooltip label={helpText} fontSize="sm" placement="right" hasArrow>
            <QuestionOutlineIcon color="gray.400" boxSize={3.5} cursor="help" />
          </Tooltip>
        )}
      </FormLabel>
      <InputGroup>
        <Input
          {...register}
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          borderRadius="lg"
          borderColor={isInvalid ? 'red.500' : 'gray.300'}
          _hover={{ borderColor: isInvalid ? 'red.600' : 'gray.400' }}
          _focus={{
            borderColor: isInvalid ? 'red.500' : 'brand.purple',
            boxShadow: isInvalid ? '0 0 0 1px #FC8181' : '0 0 0 1px #6D3CFE',
          }}
          onChange={(e) => {
            setPassword(e.target.value)
            register.onChange(e)
          }}
          onBlur={(e) => {
            setIsTouched(true)
            register.onBlur(e)
          }}
        />
        <InputRightElement width="4.5rem">
          <Button h="1.75rem" size="sm" onClick={handleClick} variant="ghost">
            {show ? <ViewOffIcon /> : <ViewIcon />}
          </Button>
        </InputRightElement>
      </InputGroup>

      {showFeedback && (
        <VStack align="stretch" mt={2} spacing={2}>
          <HStack>
            <Progress
              value={strengthPercent}
              size="sm"
              colorScheme={strengthColor}
              flex={1}
              borderRadius="full"
            />
            <Text fontSize="xs" fontWeight="medium" color={`${strengthColor}.600`} w="60px">
              {strengthLabel}
            </Text>
          </HStack>
          <VStack align="start" spacing={1} fontSize="xs" color="gray.600">
            <HStack spacing={1}>
              <CheckIcon color={checks.length ? 'green.500' : 'gray.300'} boxSize={3} />
              <Text>At least 8 characters</Text>
            </HStack>
            <HStack spacing={1}>
              <CheckIcon color={checks.uppercase ? 'green.500' : 'gray.300'} boxSize={3} />
              <Text>One uppercase letter</Text>
            </HStack>
            <HStack spacing={1}>
              <CheckIcon color={checks.lowercase ? 'green.500' : 'gray.300'} boxSize={3} />
              <Text>One lowercase letter</Text>
            </HStack>
            <HStack spacing={1}>
              <CheckIcon color={checks.number ? 'green.500' : 'gray.300'} boxSize={3} />
              <Text>One number</Text>
            </HStack>
          </VStack>
        </VStack>
      )}

      {isInvalid && errorMessage && (
        <FormErrorMessage fontSize="sm">{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  )
}
